import {
  FunctionComponent,
  JSXElementConstructor,
  ReactElement,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  MenuItem,
  Page,
  PageConfiguration,
} from "@fyrstain/fhir-front-library";
import i18n from "i18next";
import UserService from "../../services/UserService";
import { toPublicUrl } from "../../services/PublicUrl";
import "./SaturnePage.css";

interface SaturnePageProps {
  titleKey?: string;
  loading?: boolean;
  children?: ReactElement<any, string | JSXElementConstructor<any>>;
  needsLogin: boolean;
  wide?: boolean;
}

const SaturnePage: FunctionComponent<SaturnePageProps> = (props) => {
  const navigate = useNavigate();

  const handleLogin = useCallback(
    (config: MenuItem) => {
      if (config.needsLogin && !UserService.isAuthenticated()) {
        void UserService.doLogin();
        navigate("/Home");
      }
    },
    [navigate],
  );

  const handleLangChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    void i18n.changeLanguage(event.target.value);
  };

  const handleNavbarLogoClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    const target = event.target as Element;

    if (target.closest(".navbar-brand")) {
      event.preventDefault();
      navigate("/");
    }
  };

  const fullConfig: PageConfiguration = {
    language: i18n.t,
    navigationBarConfigs: {
      homeLink: toPublicUrl("/Home"),
      logoLink: toPublicUrl("/assets/Saturne.png"),
      alt: "Saturne",
      authentication: {
        handleLogin,
        token: UserService.getToken(),
        doLogin: UserService.doLogin,
        doLogout: UserService.doLogout,
        isAuthenticated: UserService.isAuthenticated,
        getUserName: UserService.getUsername,
      },
      menuItems: [
        {
          title: i18n.t("navbar.items.mySchedules"),
          link: toPublicUrl("/MySchedules"),
          needsLogin: true,
        },
        {
          title: i18n.t("navbar.items.schedules"),
          link: toPublicUrl("/Schedules"),
          needsLogin: true,
        },
        {
          title: i18n.t("navbar.items.newAppointment"),
          link: toPublicUrl("/Appointments/New"),
          needsLogin: true,
        },
      ],
      dropDownItems: [],
    },
    titleKey: props.titleKey,
    loading: props.loading,
    children: props.children,
    needsLogin: props.needsLogin,
    isAuthenticated: UserService.isAuthenticated,
    doLogin: UserService.doLogin,
    footerConfigs: {
      languages: {
        default: i18n.language,
        onChange: handleLangChange,
        options: [
          { label: "English", value: "en" },
          { label: "Français", value: "fr" },
        ],
      },
      logo: [
        {
          src: "https://fyrstain.com/wp-content/uploads/2022/10/Logo_fyrstain_horyzontal.svg",
          alt: "Fyrstain logo",
          href: "https://fyrstain.com",
        },
        ...(process.env.REACT_APP_DISPLAY_CLIENT_LOGO === "true"
          ? [
              {
                src: toPublicUrl("/assets/client_logo.jpg"),
                alt: "Client logo",
                href: toPublicUrl("/Home"),
              },
            ]
          : []),
      ],
      items: [
        {
          label: i18n.t("footer.items.about"),
          href: toPublicUrl("/InProgress"),
        },
        {
          label: i18n.t("footer.items.contact"),
          href: toPublicUrl("/InProgress"),
        },
        {
          label: i18n.t("footer.items.problemtracking"),
          href: toPublicUrl("/InProgress"),
        },
        {
          label: i18n.t("footer.items.implementationguide"),
          href: toPublicUrl("/ImplementationGuide"),
        },
      ],
    },
  };

  return (
    <div
      className={props.wide ? "saturne-page saturne-page--wide" : "saturne-page"}
      onClick={handleNavbarLogoClick}
    >
      <Page {...fullConfig} />
    </div>
  );
};

export default SaturnePage;
